// Umiestnenie: frontend/src/web/nacitanie.ts
// Načítanie verejného webu: úvodná obrazovka, prvá obrazovka naraz,
// zvyšok hneď potom a plynulý prechod medzi stránkami. Platí pre všetky
// šablóny - šablóna nemusí robiť nič.
//
// PRVÁ NÁVŠTEVA
//   1. index.html ukáže logo klubu na pozadí šablóny (#nacitanie-webu), ale
//      až po 0,3 s - rýchly web ho nikdy neukáže.
//   2. Medzitým sa načíta šablóna a dáta stránky (sledujeme fetch a XHR)
//      a stiahnu sa obrázky prvej obrazovky (vrátane ďalších snímok slidera).
//   3. Potom logo zmizne a stránka sa ukáže celá. Logo je vidieť aspoň
//      0,5 s (nebliká) a najviac do 2,5 s od otvorenia adresy - pri veľmi
//      pomalom pripojení počká aspoň na vykreslenie šablóny.
//   4. Hneď nato sa stiahnu obrázky ďalej na stránke v poradí zhora nadol,
//      nečaká sa na posúvanie. Kým nie sú hotové, ukazujú rozmazaný náhľad
//      (32 px verzia z backendu).
//
// PRECHOD MEDZI STRÁNKAMI
//   Úvodná obrazovka sa už neukazuje. Stará stránka ostane na mieste
//   (presná kópia nad webom), hore beží tenký pásik vo farbe klubu a nová
//   stránka sa ukáže, keď má hotovú prvú obrazovku - najneskôr po 1 s.

const ID_UVODNEJ = 'nacitanie-webu';

/** Úvodná obrazovka sa ukazuje od 0,3 s (oneskorenie animácie v index.html). */
const UKAZE_SA_OD = 300;
/** Keď už je vidieť, ostane aspoň takto dlho. */
const MIN_VIDITELNA = 500;
/** Najdlhšie čakanie na prvú obrazovku pri prvej návšteve (od otvorenia adresy). */
const LIMIT_PRVEJ = 2500;
/** Pri pomalom pripojení ešte chvíľu po vykreslení šablóny (dáta, obrázky). */
const PO_SABLONE = 600;
/** Najdlhší prechod medzi stránkami. */
const LIMIT_PRECHODU = 1000;
/** Bez nových požiadaviek a zmien na stránke aspoň takto dlho = obsah je vykreslený. */
const TICHO = 150;
/** Obrázky pod prvou obrazovkou: naraz najviac toľko, a najviac toľko na stránku. */
const NARAZ = 6;
const NAJVIAC_NA_STRANKU = 48;
/** Šírka rozmazaného náhľadu (backend: SIRKA_NAHLADU). */
const SIRKA_NAHLADU = 32;

// ===== Stav =====

let spustene = false;
let sablonaOd: number | null = null;
let beziacePoziadavky = 0;
let poslednaAktivita = 0;
let cakajuceCasti = 0;
let odhalene = false;
let koren: HTMLElement | null = null;

const teraz = () => performance.now();
const aktivita = () => {
  poslednaAktivita = teraz();
};

// ===== Pomocné funkcie (exportované kvôli testom) =====

/** Adresa rozmazaného náhľadu pre obrázok z /uploads, inak null. */
export const adresaNahladu = (src: string): string | null => {
  try {
    const url = new URL(src, window.location.href);
    if (url.origin !== window.location.origin || !url.pathname.startsWith('/uploads/')) return null;
    if (!/\.(jpe?g|png|webp)$/i.test(url.pathname)) return null;
    return `${url.pathname}?w=${SIRKA_NAHLADU}`;
  } catch {
    return null;
  }
};

/** Adresy obrázkov z hodnoty background-image (aj viac vrstiev). */
export const adresyZPozadia = (hodnota: string): string[] => {
  if (!hodnota || hodnota === 'none') return [];
  const adresy: string[] = [];
  const vzor = /url\(\s*(['"]?)(.*?)\1\s*\)/g;
  let m: RegExpExecArray | null;
  while ((m = vzor.exec(hodnota))) {
    if (m[2] && !m[2].startsWith('data:')) adresy.push(m[2]);
  }
  return adresy;
};

/** Leží prvok na prvej obrazovke? Vodorovne nerozhoduje - ďalšie snímky slidera patria k nej. */
export const jeNaObrazovke = (r: { top: number; bottom: number; width: number; height: number }, vyska: number): boolean =>
  (r.width > 0 || r.height > 0) && r.top < vyska && r.bottom > 0;

// ===== Sledovanie požiadaviek a zmien na stránke =====

const sledujPoziadavky = () => {
  const povodnyFetch = window.fetch;
  window.fetch = function (...args: Parameters<typeof fetch>) {
    beziacePoziadavky += 1;
    aktivita();
    return povodnyFetch.apply(window, args).finally(() => {
      beziacePoziadavky -= 1;
      aktivita();
    });
  } as typeof fetch;

  const povodnySend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, ...args: Parameters<XMLHttpRequest['send']>) {
    beziacePoziadavky += 1;
    aktivita();
    this.addEventListener(
      'loadend',
      () => {
        beziacePoziadavky -= 1;
        aktivita();
      },
      { once: true }
    );
    return povodnySend.apply(this, args);
  };
};

let naplanovanaKontrola = 0;
const sledujZmeny = () => {
  new MutationObserver(() => {
    aktivita();
    // nové obrázky (ďalšia stránka, dotiahnutý obsah) - stiahnuť a dať im náhľad
    if (!odhalene) return;
    window.clearTimeout(naplanovanaKontrola);
    naplanovanaKontrola = window.setTimeout(dotiahniZvysok, 200);
  }).observe(koren as HTMLElement, { childList: true, subtree: true });
};

/** Čaká, kým platí podmienka (kontrola každých 50 ms), najdlhšie do času `do`. */
const pockaj = (podmienka: () => boolean, kedy: number) =>
  new Promise<void>((hotovo) => {
    const over = () => {
      if (podmienka() || teraz() >= kedy) return hotovo();
      window.setTimeout(over, 50);
    };
    over();
  });

const ticho = () => cakajuceCasti === 0 && beziacePoziadavky === 0 && teraz() - poslednaAktivita >= TICHO;

const casovac = (kedy: number) => new Promise<void>((r) => window.setTimeout(r, Math.max(0, kedy - teraz())));

// ===== Prvá obrazovka =====

const pockajNaObrazok = (img: HTMLImageElement): Promise<void> => {
  if (img.loading === 'lazy') img.loading = 'eager';
  const dekoduj = () => (img.naturalWidth > 0 ? img.decode().catch(() => undefined) : undefined);
  if (img.complete) return Promise.resolve(dekoduj());
  return new Promise<void>((hotovo) => {
    const koniec = () => {
      img.removeEventListener('load', koniec);
      img.removeEventListener('error', koniec);
      Promise.resolve(dekoduj()).then(() => hotovo());
    };
    img.addEventListener('load', koniec);
    img.addEventListener('error', koniec);
  });
};

const nacitajAdresu = (adresa: string): Promise<void> =>
  new Promise((hotovo) => {
    const obr = new Image();
    obr.onload = obr.onerror = () => hotovo();
    obr.src = adresa;
  });

/** Obrázky (aj pozadia z CSS) na prvej obrazovke - sľuby, ktoré sa splnia po ich stiahnutí. */
const zdrojePrvejObrazovky = (): Promise<void>[] => {
  if (!koren) return [];
  const vyska = window.innerHeight;
  const sluby: Promise<void>[] = [];
  koren.querySelectorAll('img').forEach((img) => {
    if ((img.currentSrc || img.src || img.srcset) && jeNaObrazovke(img.getBoundingClientRect(), vyska)) sluby.push(pockajNaObrazok(img));
  });
  const pozadia = new Set<string>();
  const prvky = koren.querySelectorAll<HTMLElement>('*');
  for (let i = 0; i < prvky.length && i < 4000; i++) {
    const hodnota = getComputedStyle(prvky[i]).backgroundImage;
    if (hodnota === 'none' || !hodnota.includes('url(')) continue;
    if (!jeNaObrazovke(prvky[i].getBoundingClientRect(), vyska)) continue;
    adresyZPozadia(hodnota).forEach((a) => pozadia.add(a));
  }
  pozadia.forEach((a) => sluby.push(nacitajAdresu(a)));
  return sluby;
};

/** Počká, kým je prvá obrazovka vykreslená a jej obrázky stiahnuté (najdlhšie do času `limit`). */
const pockajNaPrvuObrazovku = async (limit: () => number) => {
  await pockaj(ticho, limit());
  const sluby = zdrojePrvejObrazovky();
  if (document.fonts?.ready) sluby.push(document.fonts.ready.then(() => undefined));
  await Promise.race([Promise.all(sluby), casovac(limit())]);
};

const skryUvodnu = () => {
  const el = document.getElementById(ID_UVODNEJ);
  if (!el) return;
  const t = teraz();
  // ešte nebola vidieť - zmizne bez animácie
  if (t < UKAZE_SA_OD) return el.remove();
  window.setTimeout(
    () => {
      el.classList.add('nw-skryva');
      window.setTimeout(() => el.remove(), 320);
    },
    Math.max(0, UKAZE_SA_OD + MIN_VIDITELNA - t)
  );
};

const prvaNavsteva = async () => {
  // Bez vykreslenej šablóny by sa ukázala prázdna stránka - na šablónu
  // čakáme aj dlhšie (poistka v index.html skryje logo po 12 s)
  await pockaj(() => sablonaOd !== null, 12000);
  const limit = () => Math.max(LIMIT_PRVEJ, (sablonaOd ?? 0) + PO_SABLONE);
  await pockajNaPrvuObrazovku(limit);
  odhalene = true;
  // čas zobrazenia stránky - v nástrojoch prehliadača (Performance) a pri meraní
  performance.mark('klubino:stranka-zobrazena');
  skryUvodnu();
  dotiahniZvysok();
};

// ===== Zvyšok stránky: obrázky po poradí a rozmazané náhľady =====

let staheVStranke = 0;
let bezi = 0;
const vybavene = new WeakSet<HTMLImageElement>();

/** Dá obrázku rozmazaný náhľad; vráti jeho adresu (alebo null). */
const dajNahlad = (img: HTMLImageElement): string | null => {
  if (img.complete || img.dataset.nwNahlad) return null;
  const adresa = adresaNahladu(img.currentSrc || img.src || img.srcset.split(/[\s,]/)[0] || '');
  if (!adresa) return null;
  const styl = getComputedStyle(img);
  // len fotky, ktoré plochu vyplnia - logá (contain) by náhľad presahoval
  if (styl.objectFit !== 'cover' || img.getBoundingClientRect().width < 80) return null;
  img.dataset.nwNahlad = '1';
  img.style.backgroundImage = `url("${adresa}")`;
  img.style.backgroundSize = 'cover';
  img.style.backgroundPosition = styl.objectPosition;
  return adresa;
};

/** Náhľady (~1 kB) sa stiahnu skôr než fotky - pri pomalom pripojení by inak čakali za nimi. */
const stiahniNahlady = (adresy: string[]): Promise<void> => {
  if (adresy.length === 0) return Promise.resolve();
  const sluby = adresy.map(
    (a) =>
      new Promise<void>((hotovo) => {
        const obr = new Image();
        obr.fetchPriority = 'high';
        obr.onload = obr.onerror = () => hotovo();
        obr.src = a;
      })
  );
  return Promise.race([Promise.all(sluby).then(() => undefined), new Promise<void>((r) => window.setTimeout(r, 800))]);
};

const odstranNahlad = (udalost: Event) => {
  const img = udalost.target;
  if (!(img instanceof HTMLImageElement) || !img.dataset.nwNahlad) return;
  delete img.dataset.nwNahlad;
  img.style.removeProperty('background-image');
  img.style.removeProperty('background-size');
  img.style.removeProperty('background-position');
};

function dotiahniZvysok() {
  if (!koren) return;
  const obrazky = Array.from(koren.querySelectorAll('img'));
  const nahlady = obrazky.map(dajNahlad).filter((a): a is string => a !== null);
  const cakajuce = obrazky.filter((img) => img.loading === 'lazy' && !img.complete && !vybavene.has(img));
  const dalsi = () => {
    while (bezi < NARAZ && staheVStranke < NAJVIAC_NA_STRANKU && cakajuce.length > 0) {
      const img = cakajuce.shift() as HTMLImageElement;
      if (!img.isConnected || vybavene.has(img)) continue;
      vybavene.add(img);
      bezi += 1;
      staheVStranke += 1;
      img.loading = 'eager';
      const koniec = () => {
        bezi -= 1;
        dalsi();
      };
      if (img.complete) koniec();
      else {
        img.addEventListener('load', koniec, { once: true });
        img.addEventListener('error', koniec, { once: true });
      }
    }
  };
  void stiahniNahlady(nahlady).then(dalsi);
}

// ===== Prechod medzi stránkami =====

let poslednaCesta = window.location.pathname;
let prechod: { vrstva: HTMLElement; pasik: HTMLElement; id: number } | null = null;

const STYL_PRECHODU = `
.nw-prechod { position: fixed; inset: 0; z-index: 2147482000; overflow: hidden; pointer-events: auto; transition: opacity 0.18s ease; }
.nw-prechod.is-prec { opacity: 0; }
.nw-prechod *, .nw-prechod *::before, .nw-prechod *::after { animation-duration: 0s !important; animation-delay: 0s !important; transition: none !important; }
.nw-pasik { position: fixed; top: 0; left: 0; right: 0; height: 3px; z-index: 2147482001; pointer-events: none;
  background: var(--club-primary, #2563eb); box-shadow: 0 0 8px color-mix(in srgb, var(--club-primary, #2563eb) 60%, transparent);
  transform-origin: 0 50%; transform: scaleX(0); transition: transform 1s cubic-bezier(0.1, 0.7, 0.3, 1), opacity 0.2s ease 0.15s; }
.nw-pasik.is-bezi { transform: scaleX(0.85); }
.nw-pasik.is-hotovo { transform: scaleX(1); opacity: 0; transition: transform 0.15s ease, opacity 0.2s ease 0.15s; }
@media (prefers-reduced-motion: reduce) { .nw-prechod { transition: none; } }
`;

/** Kópia aktuálnej stránky, ktorá ostane navrchu, kým sa nová nevykreslí. */
const kopiaStranky = (): HTMLElement => {
  const povodna = koren as HTMLElement;
  const kopia = povodna.cloneNode(true) as HTMLElement;
  // nedotiahnuté obrázky by sa v kópii zbytočne sťahovali
  const obrazky = povodna.querySelectorAll('img');
  kopia.querySelectorAll('img').forEach((img, i) => {
    if (!obrazky[i]?.complete) {
      img.removeAttribute('srcset');
      img.removeAttribute('src');
    } else {
      // hotový obrázok je v pamäti prehliadača - musí sa ukázať hneď,
      // „lazy" by ho v kópii na prvé snímky nechal prázdny
      img.loading = 'eager';
      img.decoding = 'sync';
    }
  });
  kopia.querySelectorAll('iframe, video, audio, canvas, script').forEach((el) => el.remove());
  const vrstva = document.createElement('div');
  vrstva.className = 'nw-prechod';
  vrstva.setAttribute('aria-hidden', 'true');
  vrstva.style.background = getComputedStyle(document.body).backgroundColor || 'var(--web-pozadie, #fff)';
  vrstva.appendChild(kopia);
  return vrstva;
};

const zacniPrechod = () => {
  if (!odhalene || !koren) return;
  const zaciatok = teraz();
  if (prechod) {
    // ďalší klik počas prechodu - kópia prvej stránky ostáva, čas sa predĺži
    prechod.id += 1;
  } else {
    const vrstva = kopiaStranky();
    const pasik = document.createElement('div');
    pasik.className = 'nw-pasik';
    document.body.append(vrstva, pasik);
    vrstva.scrollTop = window.scrollY;
    vrstva.scrollLeft = window.scrollX;
    requestAnimationFrame(() => requestAnimationFrame(() => pasik.classList.add('is-bezi')));
    prechod = { vrstva, pasik, id: 0 };
  }
  staheVStranke = 0;
  const id = prechod.id;
  // nová stránka sa začne vykresľovať až po zmene adresy
  aktivita();
  void pockajNaPrvuObrazovku(() => zaciatok + LIMIT_PRECHODU).then(() => {
    if (!prechod || prechod.id !== id) return;
    const { vrstva, pasik } = prechod;
    prechod = null;
    pasik.classList.add('is-hotovo');
    vrstva.classList.add('is-prec');
    window.setTimeout(() => {
      vrstva.remove();
      pasik.remove();
    }, 360);
    dotiahniZvysok();
  });
};

const sledujNavigaciu = () => {
  const styl = document.createElement('style');
  styl.textContent = STYL_PRECHODU;
  document.head.appendChild(styl);

  const povodnyPush = history.pushState;
  history.pushState = function (this: History, ...args: Parameters<History['pushState']>) {
    const url = args[2];
    const cesta = url ? new URL(String(url), window.location.href).pathname : window.location.pathname;
    // kópia sa robí ešte pred zmenou adresy - stránka je ešte stará
    if (cesta !== poslednaCesta) zacniPrechod();
    poslednaCesta = cesta;
    return povodnyPush.apply(this, args);
  };
  const povodnyReplace = history.replaceState;
  history.replaceState = function (this: History, ...args: Parameters<History['replaceState']>) {
    const vysledok = povodnyReplace.apply(this, args);
    poslednaCesta = window.location.pathname;
    return vysledok;
  };
  // Späť/dopredu: React Router prekreslí stránku až po tejto udalosti
  // (poslucháč jadra je zaregistrovaný skôr), kópia je teda ešte stará
  window.addEventListener('popstate', () => {
    if (window.location.pathname !== poslednaCesta) zacniPrechod();
    poslednaCesta = window.location.pathname;
  });
};

// ===== Verejné rozhranie jadra =====

/** Spustí sa raz pred vykreslením verejného webu (index.tsx). */
export const spustiNacitanie = (): void => {
  if (spustene) return;
  spustene = true;
  koren = document.getElementById('root');
  if (!koren) return;
  sledujPoziadavky();
  sledujZmeny();
  document.addEventListener('load', odstranNahlad, true);
  document.addEventListener('error', odstranNahlad, true);
  sledujNavigaciu();
  void prvaNavsteva();
};

/** Šablóna je načítaná a jej časti sa vykresľujú (SablonaKontext). */
export const oznacSablonuHotovu = (): void => {
  if (sablonaOd === null) {
    sablonaOd = teraz();
    performance.mark('klubino:sablona-nacitana');
  }
  aktivita();
};

/** Časť, ktorá sa ešte načítava (Suspense) - kým je pripojená, stránka nie je hotová. */
export const zacniCakanie = (): (() => void) => {
  cakajuceCasti += 1;
  aktivita();
  let skoncene = false;
  return () => {
    if (skoncene) return;
    skoncene = true;
    cakajuceCasti -= 1;
    aktivita();
  };
};
